<template>
  <v-row justify="center">
    <v-dialog
      v-model="isVisible"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      max-width="600"
      persistent
      scrollable
    >
      <v-card>
        <v-toolbar
          flat
          height="80"
          max-height="80"
        >
          <v-avatar :color="$t('Common.EmailSelectDialog.Toolbar.Color')">
            <v-icon dark>
              {{ $t('Common.EmailSelectDialog.Toolbar.Icon') }}
            </v-icon>
          </v-avatar>

          <v-toolbar-title class="pl-3">
            <div class="body-3">
              {{ $t('Common.EmailSelectDialog.Toolbar.Title') }}
            </div>
            <div class="text-caption">
              {{ $t('Common.EmailSelectDialog.Toolbar.Subtitle') }}
            </div>
          </v-toolbar-title>

          <v-spacer />

          <v-btn
            icon
            @click="onCancelButtonClick"
          >
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-divider />

        <v-card-text class="pa-1">
          <v-form
            ref="dataForm"
            v-model="dataFormValid"
            class="fill-height"
            @submit.prevent
          >
            <v-container
              :class="[{'fill-height align-start text-center': !emails.length}]"
              class="mt-0 pt-0"
              fluid
            >
              <v-row dense>
                <v-col
                  class="text-start"
                  cols="6"
                >
                  <v-menu
                    v-model="menuActive"
                    :disabled="!notificationEmailsFiltered.length"
                    :min-width="300"
                    :nudge-width="300"
                    bottom
                    offset-y
                  >
                    <template #activator="{on}">
                      <v-btn
                        :disabled="!notificationEmailsFiltered.length"
                        class="my-2"
                        color="purple"
                        dark
                        depressed
                        v-on="on"
                      >
                        {{ $t('Common.Button.Select').toLocaleUpperCase($i18n.locale) }} EMAIL

                        <v-divider
                          class="ml-2"
                          vertical
                        />
                        <v-icon right>
                          mdi-chevron-down
                        </v-icon>
                      </v-btn>
                    </template>

                    <v-list class="pa-1">
                      <template v-for="(notificationEmail, notificationEmailIdx) in notificationEmailsFiltered">
                        <v-list-item
                          :key="`notification-email-${notificationEmailIdx}`"
                          @click="addItem(notificationEmail.Email)"
                        >
                          <v-list-item-content>
                            <v-list-item-title>{{ notificationEmail.Label }}</v-list-item-title>
                            <v-list-item-subtitle>{{ notificationEmail.Email }}</v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                      </template>
                    </v-list>
                  </v-menu>
                </v-col>

                <v-col
                  class="text-end"
                  cols="6"
                >
                  <v-btn
                    v-if="canAddCustomEmail"
                    class="my-2"
                    color="primary"
                    depressed
                    @click="addItem()"
                  >
                    <v-icon left>
                      mdi-plus
                    </v-icon>

                    {{ $t('Common.Button.Add').toLocaleUpperCase($i18n.locale) }} EMAIL
                  </v-btn>
                </v-col>

                <v-col cols="12">
                  <v-divider class="mt-1 mb-2" />
                </v-col>
              </v-row>

              <template v-if="emails.length">
                <template v-for="(email, idx) in emails">
                  <v-row
                    :key="idx"
                    dense
                  >
                    <v-col>
                      <v-text-field
                        v-model="emails[idx]"
                        :clearable="(canAddCustomEmail && !emailIsDisabled(email)) || emailIsDuplicate(email)"
                        :disabled="emailIsDisabled(email) && !emailIsDuplicate(email)"
                        :hint="$t('Customer.CustomerAddEditDialog.Field.Info.Email.Hint')"
                        :label="$t('Customer.CustomerAddEditDialog.Field.Info.Email.Label')"
                        :placeholder="$t('Customer.CustomerAddEditDialog.Field.Info.Email.Label')"
                        :readonly="!canAddCustomEmail || emailIsDisabled(email)"
                        :rules="validationRules.dataForm.Email"
                        append-icon=""
                        class="mb-1"
                        flat
                        hide-details="auto"
                        outlined
                        prepend-inner-icon="mdi-email"
                      />
                    </v-col>

                    <v-col class="shrink">
                      <v-btn
                        class="mt-2"
                        color="red"
                        icon
                        @click="removeItem(idx)"
                      >
                        <v-icon>delete</v-icon>
                      </v-btn>
                    </v-col>
                  </v-row>
                </template>
              </template>

              <template v-else>
                <v-row
                  class="justify-center align-center align-content-center"
                  dense
                >
                  <v-col
                    class="pa-6"
                    cols="12"
                  >
                    <v-icon
                      color="info"
                      size="64"
                    >
                      mdi-email-plus
                    </v-icon>

                    <h3 class="mt-6">
                      {{ $t('Common.EmailSelectDialog.Toolbar.Subtitle') }}
                    </h3>
                  </v-col>
                </v-row>
              </template>
            </v-container>
          </v-form>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4">
          <v-spacer />

          <v-btn
            class="light-blue--text text--darken-1"
            outlined
            text
            @click="onCancelButtonClick"
          >
            {{ $t('Common.Button.Cancel').toLocaleUpperCase($i18n.locale) }}
          </v-btn>

          <v-btn
            :disabled="!emails?.length || !dataFormValid"
            class="green white--text elevation-0"
            @click="onSaveButtonClick"
          >
            {{ $t('Common.Button.Send').toLocaleUpperCase($i18n.locale) }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>

import AppData from '@/mixins/appdata'
import {clone, emailIsGuest} from '@/lib/helper/helper'
import DialogVisible from '@/mixins/dialogVisible.js'
import EmailTypeEnum from '@/api/Enums/EmailTypeEnum.js'

export default {
  name      : 'EmailSelectDialog',
  components: {},
  mixins    : [AppData, DialogVisible],
  props     : {
    emailAddresses: {
      type   : Array,
      default: undefined
    },

    useUserEmail: {
      type   : Boolean,
      default: false
    },

    extraData: {
      type   : Object,
      default: undefined
    },

    visible: {
      type   : Boolean,
      default: false
    }
  },
  data() {
    return {
      emails         : [],
      dataFormValid  : false,
      menuActive     : false,
      validationRules: {
        dataForm: {
          Email: [
            (v) => v && (v.length >= 0 && v.length <= 96) || this.$t('Customer.CustomerAddEditDialog.Field.Info.Email.Error.Between'),
            (v) => v && (/^\w+([.\-\\+]?\w+)*@\w+([.-]?\w+)*(\.\w{2,10})+$/.test(v)) || this.$t('Customer.CustomerAddEditDialog.Field.Info.Email.Error.Valid'),
            (v) => v && !this.emailIsDuplicate(v) || this.$t('Customer.CustomerAddEditDialog.Field.Info.Email.Error.Duplicate')
          ]
        }
      }
    }
  },
  computed: {
    notificationEmails() {
      const emails = this.appConfig?.LOCATION_DATA?.NotificationEmails || []
      const typeReceipts = this.extraData?.type === 'receiptTotalsZ'
      return emails.filter(item => item.Type.includes(EmailTypeEnum.ALL) || item.Type.includes(typeReceipts ? EmailTypeEnum.REPORT_RECEIPTS : EmailTypeEnum.REPORT_ORDERS))
    },
    notificationEmailsFiltered() {
      return this.notificationEmails.filter(item => !this.emails.includes(item.Email))
    },

    canAddCustomEmail() {
      return this.appConfig?.LOCATION_DATA?.CanAddCustomNotificationEmail ?? true
    }
  },
  watch: {
    isVisible(newVal) {
      if (newVal) {
        this.initDialog()
      } else {
        this.emails = []
      }
    }
  },
  mounted() {
  },
  created() {
  },
  beforeDestroy() {
  },
  methods: {
    initDialog() {
      if (this.canAddCustomEmail) {
        const userEmail = this.posUser?.staff_email || ''
        this.emails = Array.isArray(this.emailAddresses) ? clone(this.emailAddresses) : []
        if (this.useUserEmail && !emailIsGuest(userEmail) && !this.emails.includes(userEmail)) this.emails.push(userEmail)
        if (this.emails.length === 0) this.emails.push('')
      }
    },

    emailIsDisabled(email) {
      return !!this.notificationEmails.find(item => item.Email === email)
    },

    emailIsDuplicate(email) {
      return this.emails.filter(e => String(e).toLowerCase() === String(email).toLowerCase()).length > 1
    },

    addItem(email) {
      this.emails.push(email || '')
    },

    removeItem(idx) {
      this.emails.splice(idx, 1)
    },

    onSaveButtonClick() {
      this.$refs.dataForm.validate()

      if (this.dataFormValid) {
        this.$emit('save:dialog', this.emails, this.extraData)
        this.closeDialog()
      } else {
        this.$bus.$emit('app-show-notification', {
          body: this.$t('Common.Misc.Notification.FieldError'),
          type: 'error',
          icon: 'warning'
        })
      }
    },

    closeDialog() {
      if (this.$refs.dataForm) this.$refs.dataForm.resetValidation()
      this.isVisible = false
      this.$emit('close:dialog')
    },

    onCancelButtonClick() {
      this.$emit('cancel:dialog')
      this.closeDialog()
    }
  }
}
</script>
